import React, { useEffect, useState } from 'react';
import { storeService } from '../../services/store.service';
import { formatPhoneForDisplay } from '../../utils/phone';
// Line icons, not emoji (Lena, 11 Oct: "i don't want emoji, use icons").
import { PhoneIcon } from '../BookingNext/ReviewStep/icons';
import styles from './BookingsClosedCard.module.css';

interface BookingsClosedCardProps {
  /** The location's name, or the business's when no location is chosen. */
  name: string;
  storeId?: number | string;
  address?: string;
  signedIn: boolean;
  onSignIn: () => void;
  onAppointments: () => void;
}

/**
 * The home page when this location isn't taking online bookings
 * (mpgadmin D17). It replaces the pet-type step and "Book now", so the
 * customer isn't invited to start a booking that will be refused.
 *
 * What it offers is what still works: a call, directions, and the
 * customer's own bookings. It never says why the location is closed.
 */
const BookingsClosedCard: React.FC<BookingsClosedCardProps> = ({
  name,
  storeId,
  address,
  signedIn,
  onSignIn,
  onAppointments,
}) => {
  const [phone, setPhone] = useState<string | null>(null);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    storeService.getStoreContactNumber(storeId).then((num) => {
      if (!cancelled) setPhone(num);
    });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  return (
    <section className={styles.wrap} role="status" aria-live="polite">
      <div className={styles.card}>
        <div className={styles.icon} aria-hidden="true">
          <PhoneIcon size={26} />
        </div>
        <h2 className={styles.title}>{name} isn&rsquo;t taking online bookings right now</h2>
        <p className={styles.text}>
          {phone ? 'You can still book by phone.' : 'Please contact the salon to book.'}
        </p>
        {phone && (
          <a className={styles.primary} href={`tel:${phone.replace(/\s+/g, '')}`}>
            <PhoneIcon size={18} /> Call {formatPhoneForDisplay(phone)}
          </a>
        )}
        {address && (
          <a
            className={phone ? styles.secondary : styles.primary}
            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get directions
          </a>
        )}
      </div>
      {address && (
        <p className={styles.address}>
          <span className={styles.addressLabel}>Address</span>
          <span>{address}</span>
        </p>
      )}
      <p className={styles.signin}>
        {signedIn ? (
          <>
            Already booked?{' '}
            <button type="button" className={styles.link} onClick={onAppointments}>
              See your appointments
            </button>
          </>
        ) : (
          <>
            Already booked?{' '}
            <button type="button" className={styles.link} onClick={onSignIn}>
              Sign in
            </button>{' '}
            to see your bookings.
          </>
        )}
      </p>
    </section>
  );
};

export default BookingsClosedCard;
